import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { allProducts, productCategories, catalogTypes, type Product } from '@/mocks/products';
import ProductDrum from '@/pages/home/components/ProductDrum';

function ProductModal({ product, onClose }: { product: Product; onClose: () => void }) {
  const navigateModal = useNavigate();
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative bg-background-900 border border-background-700 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-background-900 border-b border-background-800 px-6 py-5 flex items-start justify-between gap-4 rounded-t-2xl">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-500/15 flex-shrink-0">
              <i className={`${product.icon} text-primary-400 text-2xl`}></i>
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-xs font-600 px-2 py-0.5 rounded-full text-white ${
                  product.catalogType === 'Perforación' ? 'bg-accent-600' : 'bg-primary-600'
                }`}>
                  {product.catalogType}
                </span>
                <span className="text-foreground-500 text-xs">{product.category}</span>
              </div>
              <h3 className="font-heading font-700 text-xl text-white mt-1">{product.name}</h3>
              <p className="text-foreground-400 text-sm">{product.fullName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-800 hover:bg-background-700 text-foreground-400 hover:text-white transition-all cursor-pointer flex-shrink-0"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="px-6 py-5 space-y-6">
          {/* Description */}
          <div>
            <h4 className="text-foreground-300 text-xs font-700 tracking-widest uppercase mb-2">Descripción</h4>
            <p className="text-foreground-300 text-sm leading-relaxed">{product.description}</p>
          </div>

          {/* Applications */}
          <div>
            <h4 className="text-foreground-300 text-xs font-700 tracking-widest uppercase mb-3">Aplicaciones</h4>
            <ul className="space-y-2">
              {product.applications.map((app, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center rounded-full bg-primary-500/15 flex-shrink-0 mt-0.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary-400"></div>
                  </div>
                  <span className="text-foreground-300 text-sm leading-relaxed">{app}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Specs */}
          <div>
            <h4 className="text-foreground-300 text-xs font-700 tracking-widest uppercase mb-3">Especificaciones Técnicas</h4>
            <div className="grid grid-cols-2 gap-3">
              {product.specs.map((spec, i) => (
                <div key={i} className="bg-background-800 rounded-lg px-4 py-3 flex items-center gap-2">
                  <i className="ri-checkbox-circle-line text-accent-400 text-sm flex-shrink-0"></i>
                  <span className="text-foreground-300 text-sm">{spec}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Tratamiento Recomendado */}
          {product.treatment && (
            <div>
              <h4 className="text-foreground-300 text-xs font-700 tracking-widest uppercase mb-3">Tratamiento Recomendado</h4>
              <div className="bg-background-800 rounded-xl p-4 border border-background-700">
                <p className="text-foreground-300 text-sm leading-relaxed">{product.treatment}</p>
              </div>
            </div>
          )}

          {/* Safety note */}
          <div className="bg-primary-500/8 border border-primary-500/20 rounded-xl p-4 flex items-start gap-3">
            <i className="ri-shield-check-line text-primary-400 text-lg flex-shrink-0 mt-0.5"></i>
            <p className="text-foreground-400 text-xs leading-relaxed">
              Este producto requiere el uso de equipos de protección personal (EPP) adecuados — guantes, gafas de seguridad y ropa apropiada. Almacenar en lugar fresco y seco, alejado de fuentes de calor. Dosificar correctamente y desechar según normativas ambientales locales.
            </p>
          </div>

          {/* CTA */}
          <button
            onClick={() => {
              onClose();
              setTimeout(() => navigateModal('/contacto'), 300);
            }}
            className="w-full py-3 rounded-xl bg-primary-500 hover:bg-primary-600 text-white text-sm font-600 transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
          >
            <i className="ri-mail-send-line"></i>
            Solicitar Cotización de este Producto
          </button>
        </div>
      </div>
    </div>
  );
}

function ProductCard({ product, onClick, index, visible }: {
  product: Product;
  onClick: () => void;
  index: number;
  visible: boolean;
}) {
  return (
    <div
      onClick={onClick}
      className={`group relative bg-background-900 border border-background-800 hover:border-primary-500/50 rounded-xl overflow-hidden cursor-pointer transition-all duration-500 flex flex-col ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 0.06 + 0.1}s` }}
    >
      {/* Highlight badge */}
      {product.badge && (
        <div className={`absolute top-3 left-3 z-10 px-2.5 py-0.5 rounded-full text-white text-xs font-600 ${product.badgeColor}`}>
          {product.badge}
        </div>
      )}

      {/* Type badge */}
      <div className="absolute top-3 right-3 z-10">
        <span className={`text-xs font-600 px-2 py-0.5 rounded-full text-white ${
          product.catalogType === 'Perforación' ? 'bg-accent-600' : 'bg-primary-600'
        }`}>
          {product.catalogType}
        </span>
      </div>

      {/* Drum visual area */}
      <div className="w-full h-48 bg-background-950 border-b border-background-800 relative flex-shrink-0">
        <ProductDrum name={product.name} category={product.category} />
      </div>

      {/* Content */}
      <div className="p-5 flex flex-col flex-1">
        {/* Category */}
        <span className="text-accent-400 text-xs font-600 tracking-wide uppercase block mb-1">
          {product.category}
        </span>

        {/* Name */}
        <h3 className="font-heading font-700 text-lg text-white mb-1 leading-snug">{product.name}</h3>
        <p className="text-foreground-500 text-xs mb-3">{product.fullName}</p>

        {/* Description excerpt */}
        <p className="text-foreground-400 text-sm leading-relaxed mb-4 line-clamp-2">
          {product.description}
        </p>

        {/* Specs chips */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {product.specs.slice(0, 3).map((spec, i) => (
            <span key={i} className="bg-background-800 text-foreground-500 text-xs px-2 py-0.5 rounded-full">
              {spec}
            </span>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-background-800 mt-auto">
          <span className="text-foreground-500 text-xs">{product.applications.length} aplicaciones</span>
          <div className="flex items-center gap-1 text-primary-400 text-sm font-600 group-hover:gap-2 transition-all">
            Ver detalles <i className="ri-arrow-right-line"></i>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ProductCatalog() {
  const [visible, setVisible] = useState(false);
  const [activeCatalog, setActiveCatalog] = useState('Todos');
  const [activeCategory, setActiveCategory] = useState('Todos');
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.05 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const filtered = allProducts.filter((p) => {
    const matchCatalog = activeCatalog === 'Todos' || p.catalogType === activeCatalog;
    const matchCategory = activeCategory === 'Todos' || p.category === activeCategory;
    const matchSearch =
      search.trim() === '' ||
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.description.toLowerCase().includes(search.toLowerCase()) ||
      p.category.toLowerCase().includes(search.toLowerCase());
    return matchCatalog && matchCategory && matchSearch;
  });

  const handleCatalogChange = (cat: string) => {
    setActiveCatalog(cat);
    setActiveCategory('Todos');
  };

  return (
    <section id="catalogo" ref={ref} className="py-20 md:py-28 bg-background-950">
      <div className="max-w-7xl mx-auto px-4 md:px-6">

        {/* Header */}
        <div className={`text-center mb-12 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-accent-400 text-sm font-600 tracking-widest uppercase mb-3 block">
            Línea Completa de Productos
          </span>
          <h2 className="font-heading font-700 text-4xl md:text-5xl text-white leading-tight mb-4">
            Catálogo <span className="text-primary-400">Técnico</span>
          </h2>
          <div className="w-16 h-1 bg-primary-500 rounded-full mx-auto mb-6"></div>
          <p className="text-foreground-400 text-base max-w-2xl mx-auto mb-8">
            {allProducts.length} productos químicos especializados para la industria petrolera venezolana, organizados por área de aplicación.
          </p>
        </div>

        {/* Catalog type toggle */}
        <div className={`flex justify-center mb-6 transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="inline-flex bg-background-800 rounded-full p-1">
            {catalogTypes.map((type) => (
              <button
                key={type}
                onClick={() => handleCatalogChange(type)}
                className={`px-5 py-2 rounded-full text-sm font-600 transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  activeCatalog === type
                    ? 'bg-primary-500 text-white'
                    : 'text-foreground-400 hover:text-white'
                }`}
              >
                {type === 'Todos' ? `Todos (${allProducts.length})` : `${type} (${allProducts.filter(p => p.catalogType === type).length})`}
              </button>
            ))}
          </div>
        </div>

        {/* Search + category filters */}
        <div className={`flex flex-col sm:flex-row gap-4 mb-8 transition-all duration-700 delay-150 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          {/* Search */}
          <div className="relative flex-1 max-w-sm">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm"></i>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar producto..."
              className="w-full pl-9 pr-4 py-2.5 bg-background-800 border border-background-700 focus:border-primary-500 text-white text-sm rounded-lg outline-none transition-all placeholder:text-foreground-600"
            />
          </div>

          {/* Category chips */}
          <div className="flex flex-wrap gap-2">
            {productCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-3 py-2 rounded-lg text-xs font-600 transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  activeCategory === cat
                    ? 'bg-accent-500/20 text-accent-300 border border-accent-500/40'
                    : 'bg-background-800 text-foreground-500 hover:text-white border border-transparent'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Results info */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-foreground-500 text-sm">
            {filtered.length === allProducts.length
              ? `${allProducts.length} productos en catálogo`
              : `${filtered.length} de ${allProducts.length} productos`}
          </p>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1.5 text-xs text-foreground-500">
              <span className="w-2 h-2 rounded-full bg-primary-500 inline-block"></span>
              Producción
            </span>
            <span className="flex items-center gap-1.5 text-xs text-foreground-500">
              <span className="w-2 h-2 rounded-full bg-accent-500 inline-block"></span>
              Perforación
            </span>
          </div>
        </div>

        {/* Products grid */}
        {filtered.length > 0 ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filtered.map((product, i) => (
              <ProductCard
                key={product.id}
                product={product}
                onClick={() => setSelectedProduct(product)}
                index={i}
                visible={visible}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <i className="ri-search-eye-line text-4xl text-foreground-700 mb-3 block"></i>
            <p className="text-foreground-500">No se encontraron productos con ese filtro.</p>
            <button
              onClick={() => { setSearch(''); setActiveCategory('Todos'); setActiveCatalog('Todos'); }}
              className="mt-4 text-primary-400 text-sm hover:text-primary-300 cursor-pointer"
            >
              Limpiar filtros
            </button>
          </div>
        )}

        {/* Bottom CTA */}
        <div className={`mt-14 text-center transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 bg-background-900 border border-background-700 rounded-2xl px-8 py-6">
            <div className="text-left">
              <p className="text-white font-600 text-base">¿Necesitas un producto específico?</p>
              <p className="text-foreground-400 text-sm">Contáctanos para cotizaciones, fichas técnicas y disponibilidad de inventario.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
              <button
                onClick={() => navigate('/contacto')}
                className="flex items-center gap-2 px-6 py-3 rounded-xl bg-primary-500 hover:bg-primary-600 text-white text-sm font-600 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-mail-send-line"></i>
                Solicitar Cotización
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Product detail modal */}
      {selectedProduct && (
        <ProductModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}
    </section>
  );
}